import { API, ApiPrefixes } from "../api";

export const ContributionApi = API.injectEndpoints({
  endpoints: (builder) => ({
    getMembersContributionHistory: builder.query({
      query: (arg = {}) => {
        const page =
          typeof arg === "object" && arg !== null ? arg.page ?? 1 : arg || 1;
        const category_kind =
          typeof arg === "object" && arg !== null
            ? arg.category_kind
            : undefined;
        const params = new URLSearchParams({
          guard: "staff",
          page: String(page),
          limit: "20",
        });
        if (category_kind) {
          params.append("category_kind", category_kind);
        }
        return {
          url:
            ApiPrefixes["contribution"] +
            `/contribution-history?${params.toString()}`,
        };
      },
      providesTags: (result, error, arg) => {
        const page =
          typeof arg === "object" && arg !== null ? arg.page ?? 1 : arg || 1;
        const kind =
          typeof arg === "object" && arg !== null
            ? arg.category_kind || "all"
            : "all";
        return [
          {
            type: "member-contributions-history",
            id: `${page}-${kind}`,
          },
        ];
      },
      cacheStrategy: "cache-and-network",
    }),

    getMembersContributionHistoryV2: builder.query({
      query: ({
        page = 1,
        limit = 20,
        member_id,
        contribution_category_id,
        payment_type_id,
        from_date,
        to_date,
        search,
      }) => {
        const params = new URLSearchParams({
          guard: "staff",
          page: page.toString(),
          limit: limit.toString(),
        });

        if (member_id) params.append("member_id", member_id);
        if (contribution_category_id)
          params.append("contribution_category_id", contribution_category_id);
        if (payment_type_id) params.append("payment_type_id", payment_type_id);
        if (from_date) params.append("from_date", from_date);
        if (to_date) params.append("to_date", to_date);
        if (search) params.append("search", search);

        return {
          url:
            ApiPrefixes["contribution"] +
            `/contribution-history-v2?${params.toString()}`,
        };
      },
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => [
        { type: "member-contributions-history-v2", id: "list" },
      ],
      cacheStrategy: "cache-and-network",
    }),

    // Totals strip plus one row per category (collected, contributors, this month, last payment).
    getContributionCategoriesSummary: builder.query({
      query: () => ({
        url: ApiPrefixes["contribution"] + `/categories-summary?guard=staff`,
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: [{ type: "member-contributions-history", id: "categories-summary" }],
    }),

    // Who has paid what into one category.
    getContributionCategoryContributors: builder.query({
      query: ({ categoryId, page = 1, limit = 20, status = "has_paid", search = "" }) => {
        const params = new URLSearchParams({
          guard: "staff",
          page: String(page),
          limit: String(limit),
          status,
        });
        if (search) params.append("search", search);
        return {
          url: ApiPrefixes["contribution"] + `/categories/${categoryId}/contributors?${params.toString()}`,
        };
      },
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => [
        { type: "member-contributions-history-by-category-id", id: `contributors-${arg.categoryId}` },
      ],
    }),

    // One member's standing per category (paid, pending, still due), for the record-a-payment form.
    getContributionMemberSummary: builder.query({
      query: (memberId) => ({
        url: ApiPrefixes["contribution"] + `/members/${memberId}/summary?guard=staff`,
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, memberId) => [
        { type: "member-contributions-history", id: `member-summary-${memberId}` },
      ],
    }),

    getMembersContributionHistoryByCategoryId: builder.query({
      query: ({ categoryId, page }) => ({
        url:
          ApiPrefixes["contribution"] +
          `/${categoryId}/breakdown/all?guard=staff&page=${page}&limit=20`,
      }),
      transformResponse: (responseData) => responseData.data,
      providesTags: (result, error, arg) => [
        { type: "member-contributions-history-by-category-id", id: arg },
      ],
      cacheStrategy: "cache-and-network",
    }),

    createMemberContribution: builder.mutation({
      query: (body) => ({
        url: ApiPrefixes["contribution"] + `/create?guard=staff`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: [
        { type: "member-contributions-history" },
        { type: "member-contributions-history-by-category-id" },
        { type: "member-contributions-history", id: "categories-summary" },
        { type: "member-contributions-history-v2", id: "list" },
        "StaffDashboard",
      ],
    }),

    searchContributionRecords: builder.query({
      query: ({ search, page }) => ({
        url:
          ApiPrefixes["contribution"] +
          `/search/all?guard=staff&page=${page}&limit=20&search=${search}`,
      }),
      transformResponse: (responseData) => {
        return responseData.data;
      },
    }),

    //Contribution under settings

    getContributionCategories: builder.query({
      query: () => ({
        url:
          ApiPrefixes["adminSettings"] +
          `/contributions_categories?guard=staff&page=1&limit=20`,
      }),
      transformResponse: (responseData) => responseData.data,
      providesTags: (result, error, arg) => [
        { type: "contribution-category", id: arg },
      ],
      cacheStrategy: "cache-and-network",
    }),

    createContributionCategory: builder.mutation({
      query: (body) => ({
        url:
          ApiPrefixes["adminSettings"] +
          `/contributions_categories/create?guard=staff`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: [
        { type: "member-contributions-history" },
        { type: "contribution-category" },
      ],
    }),

    archiveContributionCategory: builder.mutation({
      query: (categoryId) => ({
        url:
          ApiPrefixes["adminSettings"] +
          `/contributions_categories/${categoryId}/archive?guard=staff`,
        method: "PUT",
        // body: body,
      }),
      invalidatesTags: [
        { type: "member-contributions-history" },
        { type: "contribution-category" },
      ],
    }),

    updateContributionCategory: builder.mutation({
      query: (credential) => ({
        url:
          ApiPrefixes["adminSettings"] +
          `/contributions_categories/${credential.id}/update?guard=staff`,
        method: "PATCH",
        body: credential,
      }),
      invalidatesTags: [
        { type: "member-contributions-history" },
        { type: "contribution-category" },
      ],
    }),
    deleteContributionCategory: builder.mutation({
      query: (credential) => ({
        url:
          ApiPrefixes["adminSettings"] +
          `/contributions_categories/${credential.id}/archive?guard=staff`,
        method: "PUT",
        body: credential.payload,
      }),
      invalidatesTags: [
        { type: "member-contributions-history" },
        { type: "contribution-category" },
      ],
    }),

    getCompanyContributionCategories: builder.query({
      query: () => ({
        url: ApiPrefixes["adminSettings"] + `/contributions_categories/company?guard=staff`,
      }),
      transformResponse: (responseData) => responseData?.data || [],
      providesTags: (result, error, arg) => [
        { type: "company-contribution-categories-list", id: arg },
      ],
    }),

    importContributionCategories: builder.mutation({
      query: (payload) => ({
        url: ApiPrefixes["adminSettings"] + `/contributions_categories/import?guard=staff`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "contribution-category" },
      ],
    }),

    // Savings Withdrawal Requests (Admin/Staff)
    getWithdrawalRequests: builder.query({
      query: ({ status = 'all', page = 1, limit = 20 }) => {
        const params = new URLSearchParams({
          guard: "staff",
          status: status,
          page: page.toString(),
          limit: limit.toString(),
        });

        return {
          url:
            ApiPrefixes["contribution"] +
            `/savings/withdrawal-requests?${params.toString()}`,
        };
      },
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => [
        { type: "withdrawal-requests", id: `${arg?.status || 'all'}-${arg?.page || 1}` },
      ],
      cacheStrategy: "cache-and-network",
    }),

    approveWithdrawalRequest: builder.mutation({
      query: (requestId) => ({
        url:
          ApiPrefixes["contribution"] +
          `/savings/withdrawal-requests/${requestId}/approve?guard=staff`,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "withdrawal-requests" },
        "StaffDashboard",
      ],
    }),

    declineWithdrawalRequest: builder.mutation({
      query: ({ requestId, decline_reason }) => ({
        url:
          ApiPrefixes["contribution"] +
          `/savings/withdrawal-requests/${requestId}/decline?guard=staff`,
        method: "PUT",
        body: { decline_reason },
      }),
      invalidatesTags: [
        { type: "withdrawal-requests" },
        "StaffDashboard",
      ],
    }),

    markWithdrawalAsPaid: builder.mutation({
      query: ({ requestId, payment_date, payment_reference }) => ({
        url:
          ApiPrefixes["contribution"] +
          `/savings/withdrawal-requests/${requestId}/mark-paid?guard=staff`,
        method: "PUT",
        body: {
          payment_date,
          payment_reference: payment_reference || null,
        },
      }),
      invalidatesTags: [
        { type: "withdrawal-requests" },
        "StaffDashboard",
      ],
    }),
  }),
});

export const {
  useGetMembersContributionHistoryQuery,
  useGetMembersContributionHistoryByCategoryIdQuery,
  useGetContributionCategoriesSummaryQuery,
  useGetContributionMemberSummaryQuery,
  useGetContributionCategoryContributorsQuery,
  useGetMembersContributionHistoryV2Query,
  useSearchContributionRecordsQuery,
  useCreateMemberContributionMutation,
  useGetContributionCategoriesQuery,
  useCreateContributionCategoryMutation,
  useArchiveContributionCategoryMutation,
  useUpdateContributionCategoryMutation,
  useDeleteContributionCategoryMutation,
  useGetCompanyContributionCategoriesQuery,
  useImportContributionCategoriesMutation,
  // Withdrawal Requests
  useGetWithdrawalRequestsQuery,
  useApproveWithdrawalRequestMutation,
  useDeclineWithdrawalRequestMutation,
  useMarkWithdrawalAsPaidMutation,
} = ContributionApi;
